// Images
import empty from "@/assets/images/commons/empty.png";
// Material-UI
import { Box } from "@mui/material";
// Hooks
import { useState, useEffect, useCallback, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { useSelector } from "react-redux";
import { useManagedPositions } from "@/Common/hooks/Positions";
import { useAuthorization } from "@/Common/hooks/Authorization/Authorization";
// API
import { getCSIPerformanceMetrics } from "@/Dashboard/api/queries";
// Types
import { RootState } from "@/redux/store";
// Helpers
import { capitalizeObjectKeys } from "@/Dashboard/helpers/capitalizeObjectKeys";
import { getPositionType } from "@/Common/helpers/Positions";
// Constants
import actions from "@/Constants/actionsList";
// Components
import { CustomerSatisfactionLoader } from "@/Dashboard/components/DashboardLoader";
import DashboardTabs from "@/Dashboard/components/DashboardTabs";
import { FranchiseSelector } from "@/Dashboard/components/CustomerSatisfaction/FranchiseSelector";
import { ChartLine, IChartLineHideData } from "@/Dashboard/components/CustomerSatisfaction/ChartLine";

const DEFAULT_HIDE = { Dealership: false, Region: false, You: false };
const buildFranchiseId = (index: number) => `franchise-${index}`;

export default function CustomerSatisfaction() {
	const [showYouInfo, setShowYouInfo] = useState(false);
	const locationId = useSelector((state: RootState) => state.location.location?.locationId);
	const userLogged = useSelector((state: RootState) => state.user.user);

	const { useAuth: isAuthorized } = useAuthorization();

	const { isPositionManaged } = useManagedPositions({
		queryOpts: {
			staleTime: Infinity,
		},
	});

	const isPositionManagedValue = isPositionManaged(userLogged?.position);

	const { data, isPending } = useQuery({
		queryKey: ["csiDataDashboards", locationId],
		queryFn: async () => {
			if (!locationId) {
				throw new Error("LocationId is required to fetch data");
			}
			const dataCSI = await getCSIPerformanceMetrics(locationId);
			return capitalizeObjectKeys(dataCSI ?? []);
		},
		enabled: !!locationId,
	});

	useEffect(() => {
		const exclude = ["F&I Manager"];
		if (isPositionManagedValue && !exclude.includes(userLogged?.position ?? "")) {
			setShowYouInfo(true);
		}
	}, [userLogged, isPositionManagedValue]);

	const [selectedIndex, setSelectedIndex] = useState(0);

	const [hideMap, setHideMap] = useState<Record<"Sales" | "Service", Record<string, IChartLineHideData>>>({ Sales: {}, Service: {} });

	const setHide = useCallback(
		(type: "Sales" | "Service") => (value: IChartLineHideData) => {
			setHideMap((prev) => ({
				...prev,
				[type]: {
					...prev[type],
					[buildFranchiseId(selectedIndex)]: value,
				},
			}));
		},
		[selectedIndex]
	);

	const setHideSales = useMemo(() => setHide("Sales"), [setHide]);
	const setHideService = useMemo(() => setHide("Service"), [setHide]);

	const ChartLineInstance = useCallback(
		(props: { type: string; hideValue: IChartLineHideData; hideSetter: (value: IChartLineHideData) => void }) => {
			const metrics = props.type === "sales" ? data?.[selectedIndex]?.salesMetrics : data?.[selectedIndex]?.serviceMetrics;

			return (
				<ChartLine
					chartKey={`${data?.[selectedIndex]?.franchise}-${props.type}`}
					data={metrics || []}
					showYouInfo={showYouInfo}
					hide={props.hideValue}
					setHide={props.hideSetter}
				/>
			);
		},
		[data, selectedIndex, showYouInfo]
	);

	const positionType = getPositionType(userLogged?.position);

	const canViewSales = useMemo(
		() => ["HQ", "Sales"].includes(positionType) || isAuthorized(actions.VIEW_SALES_CUSTOMER_SATISFACTION),
		[isAuthorized, positionType]
	);

	const canViewService = useMemo(
		() => ["HQ", "Service"].includes(positionType) || isAuthorized(actions.VIEW_SERVICE_CUSTOMER_SATISFACTION),
		[isAuthorized, positionType]
	);

	const initialTab = useMemo(() => {
		if (canViewSales) {
			return 0;
		}
		if (canViewService) {
			return 1;
		}
		return 2;
	}, [canViewService, canViewSales]);

	useEffect(() => {
		if (!data) {
			return;
		}

		const firstWithDataIndex = data.findIndex((metricsData) => {
			const metrics = [metricsData.salesMetrics, metricsData.serviceMetrics];
			const withValue = metrics[initialTab]?.find((metric) => metric.Dealership ?? metric.Region ?? metric.You);
			return !!withValue;
		});

		if (firstWithDataIndex !== -1) {
			setSelectedIndex(firstWithDataIndex);
			return;
		}

		setSelectedIndex(0);
	}, [data, initialTab]);

	if (isPending) {
		return <CustomerSatisfactionLoader />;
	}

	if (!data?.length) {
		return (
			<Box className="w-[200px] mx-auto">
				<img width="100%" src={empty} alt="" />
			</Box>
		);
	}

	return (
		<DashboardTabs
			tabsIdentifier="ratings"
			tabClass="!text-[13.5px] !font-medium"
			initialTab={initialTab}
			onChange={() => {}}
			data={[
				{
					tabLabel: "Sales",
					disabled: !canViewSales,
					tabContent: (
						<ChartLineInstance
							type="sales"
							hideValue={hideMap.Sales?.[buildFranchiseId(selectedIndex)] ?? DEFAULT_HIDE}
							hideSetter={setHideSales}
						/>
					),
				},
				{
					tabLabel: "Service",
					disabled: !canViewService,
					tabContent: (
						<ChartLineInstance
							type="service"
							hideValue={hideMap.Service?.[buildFranchiseId(selectedIndex)] ?? DEFAULT_HIDE}
							hideSetter={setHideService}
						/>
					),
				},
				{
					tabLabel: "",
					tabContent: <Box className="h-[180px]" />,
				},
			]}
			extraHeaderContent={
				data?.length > 0 && <FranchiseSelector data={data} selectedIndex={selectedIndex} setSelectedIndex={setSelectedIndex} />
			}
		/>
	);
}
